import { useNavigate } from "react-router-dom";
import playStoreIcon from "../assets/images/svg/mage_playstore.svg";

/* --------------------- inline social icons --------------------- */

const InstagramIcon = ({ className = "" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle
      cx="17.5"
      cy="6.5"
      r="1"
      fill="currentColor"
      stroke="none"
    />
  </svg>
);

const LinkedInIcon = ({ className = "" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 01-2.06-2.06 2.06 2.06 0 112.06 2.06zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
  </svg>
);

const XIcon = ({ className = "" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
  </svg>
);

const AppleIcon = ({ className = "" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.42C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

/* -------------------------------- footer -------------------------------- */

export default function Footer() {
  const navigate = useNavigate();

  const links: [string, string][] = [
    ["About", "/about"],
    ["How it works", "/howitworks"],
    ["Contact us", "/contactus"],
    ["Blogs", "/blog"],
    ["Privacy & Terms", "/termsandcondition"],
  ];

  const handleHomeClick = () => {
    window.history.scrollRestoration = "manual";
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
    window.location.assign("/");
  };

  return (
    <footer className="flex w-full flex-col bg-white text-black">
      {/* Top border */}
      <div className="w-full md:border-t md:border-black/20">
        <div className="mx-4 border-t border-black/20 sm:mx-8 md:mx-0 md:border-none" />
      </div>

      <div
        className="
          mx-auto w-full max-w-[1280px]
          px-4 py-8
          sm:px-8
          md:px-14 md:py-6
        "
      >
        <div className="flex flex-col items-center justify-between w-full gap-8 md:flex-row">
          
          {/* Left section: Logo and links */}
          <div className="flex flex-col items-center gap-6 md:flex-row md:gap-6 lg:gap-10">
            {/* Logo */}
            <span
              onClick={handleHomeClick}
              className="
                cursor-pointer
                font-maxwell-regular font-normal
                text-[36px] md:text-[28px]
                leading-none
                tracking-[0.03em]
                text-black
                uppercase
              "
            >
              VASTAVIK
            </span>

            {/* Navigation links */}
            <div className="flex flex-row flex-wrap items-center justify-center gap-x-4 sm:gap-x-6 gap-y-3 md:gap-7">
              {links.map(([label, path]) => (
                <span
                  key={label}
                  onClick={() => navigate(path)}
                  className="
                    cursor-pointer
                    font-productsans-light font-normal
                    text-[15px] sm:text-[16px] leading-[145%]
                    tracking-[-0.005em]
                    text-black
                    underline
                    decoration-black/80 md:decoration-black/50
                    underline-offset-[4px]
                    transition
                    hover:opacity-70
                  "
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Center section: Download the App */}
          <div className="flex flex-row items-center justify-center gap-3">
            <span className="font-productsans-bold text-[15px] font-semibold sm:text-[16px] text-black whitespace-nowrap">
              Download the App
            </span>

            {/* Vertical Divider */}
            <div className="h-[22px] w-px bg-black/30 mx-0.5" />

            <a
              href="https://apps.apple.com/in/app/vastavik/id6756848799"
              target="_blank"
              rel="noreferrer"
              aria-label="Download on the Apple Store"
              className="flex items-center justify-center w-[34px] h-[34px] border border-black/30 rounded-[6px] transition hover:bg-black/5"
            >
              <AppleIcon className="h-[18px] w-[18px] text-black" />
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.vastavik&pcampaignid=web_share"
              target="_blank"
              rel="noreferrer"
              aria-label="Get it on Google Play"
              className="flex items-center justify-center w-[34px] h-[34px] border border-black/30 rounded-[6px] transition hover:bg-black/5"
            >
              <img
                src={playStoreIcon}
                alt=""
                aria-hidden="true"
                className="h-[18px] w-[18px] object-contain"
              />
            </a>
          </div>

          {/* Right section: Social icons */}
          <div className="flex flex-row items-center justify-center gap-6 text-black md:gap-5">
            <a href="https://www.instagram.com/vastavik_org/" target="_blank" rel="noreferrer" aria-label="Instagram">
              <InstagramIcon className="h-[24px] w-[24px] md:h-[20px] md:w-[20px] transition hover:opacity-70" />
            </a>

            <a href="https://www.linkedin.com/company/vastavik-org/?viewAsMember=true" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <LinkedInIcon className="h-[24px] w-[24px] md:h-[20px] md:w-[20px] transition hover:opacity-70" />
            </a>

            <a href="https://x.com/vastavik_org" target="_blank" rel="noreferrer" aria-label="X">
              <XIcon className="h-[23px] w-[23px] md:h-[19px] md:w-[19px] transition hover:opacity-70" />
            </a>
          </div>

        </div>
      </div>

      {/* Bottom divider */}
      <div className="w-full md:border-t md:border-black/20 mt-2 md:mt-0">
        <div className="mx-4 border-t border-black/20 sm:mx-8 md:mx-0 md:border-none" />
      </div>

      {/* Copyright */}
      <div
        className="
          px-4 py-6 md:py-4
          text-center
          font-productsans-regular font-normal
          text-[13px]
          tracking-[-0.01em]
          text-black
        "
      >
        Copyright © 2025 ADROITX LIMITED. All rights reserved.
      </div>
    </footer>
  );
}